import { Component } from '@theme/component';

/**
 * @typedef {object} ProductDescriptionReadMoreRefs
 * @property {HTMLElement} description
 * @property {HTMLElement} remainder
 * @property {HTMLButtonElement} toggleButton
 * @property {HTMLElement} toggleLabel
 */

/** @extends {Component<ProductDescriptionReadMoreRefs>} */
class ProductDescriptionReadMore extends Component {
  requiredRefs = ['description', 'remainder', 'toggleButton', 'toggleLabel'];

  connectedCallback() {
    super.connectedCallback();
    this.#enhanceDescription();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#enhanceDescription();
  }

  #enhanceDescription() {
    const { description, remainder, toggleButton } = this.refs;
    if (!description || !remainder || !toggleButton || remainder.childNodes.length > 0) return;

    const firstParagraph = Array.from(description.querySelectorAll('p')).find(
      (paragraph) => paragraph.textContent?.trim() || paragraph.children.length > 0
    );

    if (!firstParagraph) return;

    const range = document.createRange();
    range.setStartAfter(firstParagraph);
    range.setEnd(description, description.childNodes.length);

    const hiddenContent = range.extractContents();
    const hasHiddenContent =
      hiddenContent.textContent?.trim() ||
      hiddenContent.querySelector('img, video, iframe, table, ul, ol, blockquote, details');

    if (!hasHiddenContent) {
      description.append(hiddenContent);
      return;
    }

    remainder.append(hiddenContent);
    toggleButton.hidden = false;
  }

  toggle() {
    const { remainder, toggleButton, toggleLabel } = this.refs;
    const expanded = remainder.hidden;

    remainder.hidden = !expanded;
    toggleButton.setAttribute('aria-expanded', String(expanded));
    toggleLabel.textContent = expanded ? this.dataset.readLessLabel ?? '' : this.dataset.readMoreLabel ?? '';
  }
}

if (!customElements.get('product-description-read-more')) {
  customElements.define('product-description-read-more', ProductDescriptionReadMore);
}
